import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subject, Subscription } from 'rxjs';
import { ActionsSubject } from '@ngrx/store';
import { ofType } from '@ngrx/effects';
import {
    actionAddResources,
    actionGetAIImage,
    actionGetAIImageReply,
    actionGetAIImages,
    actionGetAIImagesReply,
    actionGetAIVideo,
    actionGetAIVideoReply,
    actionPollVideoStatus,
    actionPollVideoStatusReply,
} from '../../store/actions/user.actions';
import { AppService, LocalStoreService, StoreSelectorsService } from '../index';
import { FileObj, ICRUDRecord, IResource, UploadStatus } from '../../interfaces';
import { ToastrService } from 'ngx-toastr';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslocoService } from '@ngneat/transloco';
import { formatBytes, TOAST_FASTER } from '../../common';
import * as _ from 'lodash';

/**
 * Interface for AI usage statistics from server
 */
export interface IAIUsageStats {
    count: number;
    remaining: number;
    max_calls: number;
    ai_type?: string;
}

/**
 * Interface for server AI image response (single image from Veo)
 */
export interface IAIImageServerResponse {
    success?: boolean;
    imageData?: string;
    mimeType?: string;
    usage?: {
        count: number;
        remaining: number;
        max_daily: number;
    };
    error?: string;
}

/**
 * Interface for DALL-E image item in array response
 */
export interface IDALLEImageItem {
    url?: string;           // DALL-E URL format
    b64_json?: string;      // GPT-image base64 format
    imageData?: string;     // Veo base64 format
    mimeType?: string;      // Veo mime type
    usage?: {
        count: number;
        remaining: number;
        max_daily: number;
    };
}

/**
 * Interface for video generation server response
 */
export interface IVideoServerResponse {
    taskId?: string;
    status?: string;
    message?: string;
    videoUri?: string;
    usage?: {
        count: number;
        remaining: number;
        max_daily: number;
    };
    error?: string;
}

/**
 * Interface for AI-generated images
 */
export interface IAIImage {
    name: string;
    url: string;
    base64: string;
    loading: boolean;
    uploading?: boolean;
    uploadedResource?: IResource;
    backgroundSet?: boolean;
}

/**
 * Interface for video generation state
 */
export interface IVideoGenerationState {
    videoUrl: string | null;
    safeVideoUrl: SafeResourceUrl | null;
    videoUploadReady: boolean;
    taskId: string | null;
    polling: boolean;
}

/**
 * AI Text, Speech, Resource Service
 * Manages AI resource generation (images/videos) independently of component lifecycle
 * This ensures video/image generation continues even when user navigates away
 */
@Injectable({
    providedIn: 'root'
})
export class AiTsrService implements OnDestroy {
    // Image generation state
    private aiImagesSubject = new BehaviorSubject<IAIImage[]>([]);
    public aiImages$ = this.aiImagesSubject.asObservable();

    // Video generation state
    private videoStateSubject = new BehaviorSubject<IVideoGenerationState>({
        videoUrl: null,
        safeVideoUrl: null,
        videoUploadReady: false,
        taskId: null,
        polling: false
    });
    public videoState$ = this.videoStateSubject.asObservable();

    // Loading state
    private loadingSubject = new BehaviorSubject<boolean>(false);
    public loading$ = this.loadingSubject.asObservable();

    // Usage stats for images
    private imageUsageSubject = new BehaviorSubject<IAIUsageStats | null>(null);
    public imageUsage$ = this.imageUsageSubject.asObservable();

    // Usage stats for videos
    private videoUsageSubject = new BehaviorSubject<IAIUsageStats | null>(null);
    public videoUsage$ = this.videoUsageSubject.asObservable();

    // Video polling
    private videoPollingSubscription: Subscription | null = null;
    private currentVideoTaskId: string | null = null;
    private lastToastTime = 0;

    // Upload tracking
    private processedFileIds = new Set<string>();
    private pendingMiniModeAction: 'background' | 'block' | null = null;

    // Subscriptions
    private subscriptions: Subscription[] = [];

    constructor(
        private sanitizer: DomSanitizer,
        private storeSelector: StoreSelectorsService,
        private localStore: LocalStoreService,
        private toastr: ToastrService,
        private appService: AppService,
        private actionsSubj: ActionsSubject,
        private translocoService: TranslocoService,
    ) {
        this.initializeSubscriptions();
    }

    /**
     * Initialize all NgRx action subscriptions
     */
    private initializeSubscriptions(): void {
        this.subscribeImagesReply();
        this.subscribeToDalleImageBlobReply();
        this.subscribeToVeroVideoReply();
        this.subscribeToVideoStatusReply();
        this.subscribeToUploadCompletion();
    }

    /**
     * Subscribe to AI images reply action
     */
    private subscribeImagesReply(): void {
        const sub = this.actionsSubj
            .pipe(ofType(actionGetAIImagesReply))
            .subscribe((reply) => {
                // Handle error cases
                if (reply?.payload?.error) {
                    this.loadingSubject.next(false);
                    this.toastr.warning(reply.payload.error);
                    return;
                }

                // Handle empty payload
                if (_.isEmpty(reply.payload)) {
                    this.loadingSubject.next(false);
                    this.toastr.info(this.translocoService.translate("general.pleaseUpgrade3", {}, this.translocoService.getActiveLang()));
                    return;
                }

                // Check for single image response format first
                if (reply.payload?.imageData && reply.payload?.success && reply.payload.imageData.length > 0) {
                    const mimeType = reply.payload.mimeType || "image/png";
                    const extension = mimeType.split("/")[1] || "png";
                    this.aiImagesSubject.next([{
                        name: `ai_image_${Date.now()}.${extension}`,
                        url: "",
                        base64: `data:${mimeType};base64,${reply.payload.imageData}`,
                        loading: false,
                    }]);
                    this.loadingSubject.next(false);

                    // Extract and store usage stats if present
                    if (reply.payload.usage) {
                        this.imageUsageSubject.next({
                            count: reply.payload.usage.count,
                            remaining: reply.payload.usage.remaining,
                            max_calls: reply.payload.usage.max_daily,
                            ai_type: 'image'
                        });
                    }

                    // Show success toast - full width at top for more attention
                    this.toastr.success(
                        "AI image created successfully! Ready to use.",
                        "Success",
                        {
                            timeOut: 6000,
                            closeButton: true,
                            positionClass: 'toast-top-full-width',
                            tapToDismiss: true,
                            progressBar: true,
                            progressAnimation: 'increasing'
                        }
                    );
                    return;
                }

                // Validate the payload structure for array format
                if (!Array.isArray(reply.payload) || reply.payload.length === 0) {
                    this.toastr.warning("AI " + this.translocoService.translate("general.wasNotAbleGenImage", {}, this.translocoService.getActiveLang()));
                    this.clearImages();
                    this.loadingSubject.next(false);
                    return;
                }

                const images = reply.payload
                    .map((item: IDALLEImageItem, index) => {
                        // Extract usage stats from first item (all items have same usage)
                        if (index === 0 && item?.usage) {
                            this.imageUsageSubject.next({
                                count: item.usage.count,
                                remaining: item.usage.remaining,
                                max_calls: item.usage.max_daily,
                                ai_type: 'image'
                            });
                        }

                        if (item?.url) { // DALL·E-3 (URL) variant
                            return {
                                name: this.extractImageName(item.url),
                                url: item.url,
                                base64: "",
                                loading: true, // Needs blob download
                            };
                        } else if (item?.b64_json) { // GPT-image (Base64) variant
                            return {
                                name: `ai_image_${Date.now()}_${index}.png`,
                                url: "",
                                base64: `data:image/png;base64,${item.b64_json}`,
                                loading: false,
                            };
                        } else if (item?.imageData && item?.mimeType) { // New format with imageData and mimeType
                            const extension = item.mimeType.split("/")[1] || "png";
                            return {
                                name: `ai_image_${Date.now()}_${index}.${extension}`,
                                url: "",
                                base64: `data:${item.mimeType};base64,${item.imageData}`,
                                loading: false,
                            };
                        }
                        console.warn("Received unexpected item format in DALL-E reply:", item);
                        return null;
                    })
                    .filter(img => img !== null) as IAIImage[];

                this.aiImagesSubject.next(images);

                // Check if any images need blob downloading
                const firstImageToLoad = images.find(img => img.loading && img.url);
                if (firstImageToLoad) {
                    this.storeSelector.dispatch(actionGetAIImage({ prompt: firstImageToLoad.url }));
                } else {
                    // All images are ready (no blob download needed)
                    this.loadingSubject.next(false);

                    // Show success toast - full width at top for more attention
                    const imageCount = images.length;
                    const message = imageCount === 1
                        ? "AI image created successfully! Ready to use."
                        : `${imageCount} AI images created successfully! Ready to use.`;
                    this.toastr.success(
                        message,
                        "Success",
                        {
                            timeOut: 6000,
                            closeButton: true,
                            positionClass: 'toast-top-full-width',
                            tapToDismiss: true,
                            progressBar: true,
                            progressAnimation: 'increasing'
                        }
                    );
                }
            });
        this.subscriptions.push(sub);
    }

    /**
     * Subscribe to DALL·E image blob reply
     */
    private subscribeToDalleImageBlobReply(): void {
        const sub = this.actionsSubj
            .pipe(ofType(actionGetAIImageReply))
            .subscribe((reply: any) => {
                if (!reply.url || !reply.payload || !(reply.payload instanceof Blob)) {
                    console.error("Received invalid DALL-E image blob reply:", reply);
                    if (reply.url) {
                        const images = this.aiImagesSubject.value;
                        // Immutable update - create new array with updated object
                        const updatedImages = images.map(img =>
                            img.url === reply.url
                                ? { ...img, loading: false }
                                : img
                        );
                        this.aiImagesSubject.next(updatedImages);
                    }
                    if (!this.aiImagesSubject.value.some(i => i.loading)) {
                        this.loadingSubject.next(false);
                    }
                    return;
                }

                const blob = reply.payload as Blob;
                const imageUrl = reply.url;
                const reader = new FileReader();

                reader.onload = (e) => {
                    const images = this.aiImagesSubject.value;
                    const base64Result = e.target?.result as string ?? "";

                    // Immutable update - create new array with updated object
                    const updatedImages = images.map(img =>
                        img.url === imageUrl
                            ? { ...img, base64: base64Result, loading: false }
                            : img
                    );

                    // Find the next image that still needs loading
                    const nextImageToLoad = updatedImages.find(img => img.loading && img.url);
                    if (nextImageToLoad) {
                        this.storeSelector.dispatch(actionGetAIImage({ prompt: nextImageToLoad.url }));
                    } else {
                        // All images are now loaded
                        this.loadingSubject.next(false);

                        // Show success toast - full width at top for more attention
                        const imageCount = updatedImages.filter(img => !img.loading).length;
                        const message = imageCount === 1
                            ? "AI image created successfully! Ready to use."
                            : `${imageCount} AI images created successfully! Ready to use.`;
                        this.toastr.success(
                            message,
                            "Success",
                            {
                                timeOut: 6000,
                                closeButton: true,
                                positionClass: 'toast-top-full-width',
                                tapToDismiss: true,
                                progressBar: true,
                                progressAnimation: 'increasing'
                            }
                        );
                    }

                    // Emit updated array
                    this.aiImagesSubject.next(updatedImages);
                };

                reader.onerror = (error) => {
                    console.error("FileReader error reading image blob:", error);
                    const images = this.aiImagesSubject.value;

                    // Immutable update
                    const updatedImages = images.map(img =>
                        img.url === imageUrl
                            ? { ...img, loading: false }
                            : img
                    );

                    if (!updatedImages.some(i => i.loading)) {
                        this.loadingSubject.next(false);
                    }

                    this.aiImagesSubject.next(updatedImages);
                };

                reader.readAsDataURL(blob);
            });
        this.subscriptions.push(sub);
    }

    /**
     * Subscribe to video generation reply
     */
    private subscribeToVeroVideoReply(): void {
        const sub = this.actionsSubj
            .pipe(ofType(actionGetAIVideoReply))
            .subscribe((payload: any) => {
                if (payload?.reply?.error) {
                    this.loadingSubject.next(false);
                    this.resetVideoState();
                    this.toastr.error(
                        payload.reply.error,
                        "creation failed",
                        { timeOut: 0, closeButton: true },
                    );
                    return;
                }
                if (_.isEmpty(payload.reply)) {
                    this.loadingSubject.next(false);
                    this.resetVideoState();
                    return this.toastr.info(this.translocoService.translate("general.pleaseUpgrade3", {}, this.translocoService.getActiveLang()));
                }

                // Extract usage stats if present
                if (payload?.reply?.usage) {
                    this.videoUsageSubject.next({
                        count: payload.reply.usage.count,
                        remaining: payload.reply.usage.remaining,
                        max_calls: payload.reply.usage.max_daily,
                        ai_type: 'video'
                    });
                }

                // New async polling flow - check for taskId
                if (payload?.reply?.taskId) {
                    const taskId = payload.reply.taskId;
                    this.currentVideoTaskId = taskId;
                    this.toastr.info("Video generation started. Polling for status...", "", {
                        timeOut: 5000,
                        closeButton: true,
                        progressBar: true
                    });

                    // Start polling for video status
                    this.startVideoPolling(taskId);
                } else if (payload?.reply?.videoUri) {
                    // Legacy synchronous flow - direct videoUri
                    this.loadingSubject.next(false);
                    const receivedVideoUrl = payload.reply.videoUri;
                    try {
                        new URL(receivedVideoUrl); // Validate URL
                        this.updateVideoState({
                            videoUrl: receivedVideoUrl,
                            safeVideoUrl: this.sanitizer.bypassSecurityTrustResourceUrl(receivedVideoUrl),
                            videoUploadReady: true,
                            taskId: null,
                            polling: false
                        });
                        this.toastr.info("Video created successfully. Ready for upload.", "", {
                            timeOut: 5000,
                            closeButton: true,
                            progressBar: true
                        });
                    } catch (e) {
                        console.error("Received invalid video URL:", receivedVideoUrl, e);
                        this.loadingSubject.next(false);
                        this.resetVideoState();
                        this.toastr.error("Received an invalid URL for the video.", "Video Creation Failed");
                    }
                } else {
                    this.loadingSubject.next(false);
                    this.resetVideoState();
                    const errorMsg = payload?.reply?.error || "Unknown error creating video";
                    this.toastr.error(`Video Creation Failed: ${errorMsg}`);
                }
            });
        this.subscriptions.push(sub);
    }

    /**
     * Start polling for video generation status
     */
    private startVideoPolling(taskId: string): void {
        this.stopVideoPolling();

        const POLL_INTERVAL_MS = 5000;

        this.videoPollingSubscription = interval(POLL_INTERVAL_MS)
            .subscribe(() => {
                this.storeSelector.dispatch(actionPollVideoStatus({ taskId }));
            });

        // Update state to show polling is active
        this.updateVideoState({
            ...this.videoStateSubject.value,
            taskId,
            polling: true
        });

        // Trigger first poll immediately
        this.storeSelector.dispatch(actionPollVideoStatus({ taskId }));
    }

    /**
     * Subscribe to video status polling replies
     */
    private subscribeToVideoStatusReply(): void {
        const sub = this.actionsSubj
            .pipe(ofType(actionPollVideoStatusReply))
            .subscribe((action: any) => {
                const status = action.status;

                // DEBUG: Log the entire status received
                console.log('[DEBUG] Video status received:', JSON.stringify(status, null, 2));

                if (status?.error) {
                    console.error("Video polling error:", status.error);
                    this.stopVideoPolling();
                    this.loadingSubject.next(false);
                    this.resetVideoState();
                    this.toastr.error(`Video generation failed: ${status.error}`, "Error", { timeOut: 0, closeButton: true });
                    return;
                }

                if (!status || !status.taskId) {
                    console.warn("Invalid status received:", status);
                    return;
                }

                // Display toast with elapsed time (throttle to avoid spam)
                const now = Date.now();
                const elapsed = status.elapsed || 0;
                if (now - this.lastToastTime > 10000) {
                    this.lastToastTime = now;
                    const elapsedMin = Math.floor(elapsed / 60);
                    const elapsedSec = elapsed % 60;
                    const timeStr = elapsedMin > 0
                        ? `${elapsedMin}m ${elapsedSec}s`
                        : `${elapsedSec}s`;
                    this.toastr.info(
                        `Video generation in progress... (${timeStr}) - Status: ${status.status || 'PROCESSING'}`,
                        "",
                        {
                            timeOut: 5000,
                            closeButton: true,
                            progressBar: true
                        }
                    );
                }

                // DEBUG: Log completion check details
                console.log('[DEBUG] Completed check:', status.completed === true);
                console.log('[DEBUG] Completed value:', status.completed);
                console.log('[DEBUG] VideoUri present:', !!status.videoUri);
                console.log('[DEBUG] VideoUri value:', status.videoUri);

                // Check if video generation is complete
                if (status.completed === true) {
                    console.log('[DEBUG] Entered completion block - status.completed === true');
                    this.stopVideoPolling();
                    this.loadingSubject.next(false);

                    if (status.videoUri) {
                        console.log('[DEBUG] VideoUri validation passed, proceeding to show success toast');
                        try {
                            new URL(status.videoUri); // Validate URL
                            this.updateVideoState({
                                videoUrl: status.videoUri,
                                safeVideoUrl: this.sanitizer.bypassSecurityTrustResourceUrl(status.videoUri),
                                videoUploadReady: true,
                                taskId: null,
                                polling: false
                            });

                            const elapsedMin = Math.floor(elapsed / 60);
                            const elapsedSec = elapsed % 60;
                            const timeStr = elapsedMin > 0
                                ? `${elapsedMin}m ${elapsedSec}s`
                                : `${elapsedSec}s`;

                            console.log('[DEBUG] About to show success toast with message:', `Video created successfully in ${timeStr}! Ready for upload.`);

                            try {
                                const toastRef = this.toastr.success(
                                    `Video created successfully in ${timeStr}! Ready for upload.`,
                                    "Success",
                                    {
                                        timeOut: 8000,
                                        closeButton: true,
                                        positionClass: 'toast-top-center',
                                        tapToDismiss: true,
                                        progressBar: true,
                                        progressAnimation: 'increasing',
                                        disableTimeOut: false,
                                        easeTime: 300,
                                        enableHtml: true
                                    }
                                );
                                console.log('[DEBUG] Toast reference created:', toastRef);
                            } catch (toastError) {
                                console.error('[DEBUG] Error creating toast:', toastError);
                            }
                        } catch (e) {
                            console.error("Received invalid video URL:", status.videoUri, e);
                            this.toastr.error("Received an invalid URL for the video.", "Video Creation Failed");
                            this.resetVideoState();
                        }
                    } else if (status.error) {
                        this.resetVideoState();
                        this.toastr.error(
                            `Video generation failed: ${status.error}`,
                            "Error",
                            { timeOut: 0, closeButton: true }
                        );
                    } else {
                        this.resetVideoState();
                        this.toastr.warning("Video generation completed but no video URL received.", "Warning");
                    }
                }
            });
        this.subscriptions.push(sub);
    }

    /**
     * Subscribe to upload completion events
     */
    private subscribeToUploadCompletion(): void {
        const sub = this.appService.getUploadingFiles().subscribe((uploadingFiles: FileObj[] | null) => {
            if (!uploadingFiles || uploadingFiles.length === 0) return;

            const newlyCompleted = uploadingFiles.find(f =>
                f.uploadStatus === UploadStatus.SUCCESS &&
                f.fileId &&
                !this.processedFileIds.has(f.fileId),
            );

            if (newlyCompleted) {
                this.processedFileIds.add(newlyCompleted.fileId);
                this.createResourceByUploading(newlyCompleted);
            }

            // Cleanup processedFileIds set
            const currentFileIdsInService = new Set(
                uploadingFiles.map(f => f.fileId).filter(id => !!id),
            );
            this.processedFileIds.forEach(processedId => {
                if (!currentFileIdsInService.has(processedId)) {
                    this.processedFileIds.delete(processedId);
                }
            });
        });
        this.subscriptions.push(sub);
    }

    /**
     * Stop video polling
     */
    private stopVideoPolling(): void {
        if (this.videoPollingSubscription) {
            this.videoPollingSubscription.unsubscribe();
            this.videoPollingSubscription = null;
        }
        this.currentVideoTaskId = null;
        this.lastToastTime = 0;
    }

    /**
     * Reset video state
     */
    private resetVideoState(): void {
        this.stopVideoPolling();
        this.videoStateSubject.next({
            videoUrl: null,
            safeVideoUrl: null,
            videoUploadReady: false,
            taskId: null,
            polling: false
        });
    }

    /**
     * Update video state
     */
    private updateVideoState(state: IVideoGenerationState): void {
        this.videoStateSubject.next(state);
    }

    /**
     * Clear images
     */
    public clearImages(): void {
        this.aiImagesSubject.next([]);
        this.resetVideoState();
        this.loadingSubject.next(false);
    }

    /**
     * Upload image to resource
     */
    public async uploadImageToResource(selectedImage: IAIImage, skipAutoBackground = false): Promise<void> {
        if (!selectedImage || !selectedImage.base64) {
            this.toastr.error("Cannot upload - image data is missing.", "", TOAST_FASTER);
            return;
        }

        // Create immutable update - map to new array with updated object
        const images = this.aiImagesSubject.value;
        const updatedImages = images.map(img =>
            img.name === selectedImage.name
                ? { ...img, uploading: true }
                : img
        );
        this.aiImagesSubject.next(updatedImages);

        this.toastr.info("Preparing image for upload...", "", TOAST_FASTER);
        this.appService.setUploadingStatus({ process: 0, show: true });

        try {
            const fileName = selectedImage.name || `ai_image_${Date.now()}`;
            const fileInfo = await this.convertToFileFromImageUrlOrBase64(selectedImage.base64, fileName);

            if (!fileInfo || !fileInfo.fileObj) {
                throw new Error("Could not convert image data to a file object.");
            }

            const fileToUpload: FileObj = {
                fileName: fileInfo.fileName,
                fileType: fileInfo.fileType,
                file: fileInfo.fileObj,
                fileId: `${fileInfo.fileName}-${fileInfo.fileObj.lastModified || Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                progress: 0,
                uploadStatus: UploadStatus.READY,
                formattedSize: formatBytes(fileInfo.fileObj.size, 2),
                fileSize: fileInfo.fileObj.size,
                skipAutoBackground: skipAutoBackground
            };
            this.appService.setUploadingFiles([fileToUpload]);
            this.appService.uploadResourceFiles();

        } catch (error: any) {
            console.error("Error preparing image for upload:", error);
            this.toastr.error(`Failed to prepare image: ${error.message || "Unknown error"}`, "", TOAST_FASTER);
            this.appService.setUploadingStatus({ process: 0, show: false });

            // Reset uploading state immutably
            const currentImages = this.aiImagesSubject.value;
            const resetImages = currentImages.map(img =>
                img.name === selectedImage.name
                    ? { ...img, uploading: false }
                    : img
            );
            this.aiImagesSubject.next(resetImages);
        }
    }

    /**
     * Upload video to resource
     */
    public async uploadVideoToResource(): Promise<void> {
        const videoState = this.videoStateSubject.value;
        if (!videoState.videoUrl || !videoState.videoUploadReady) {
            this.toastr.error("No video ready for upload.", "", TOAST_FASTER);
            return;
        }

        this.toastr.info("Preparing video for upload...", "", TOAST_FASTER);
        this.appService.setUploadingStatus({ process: 0, show: true });

        try {
            let potentialName = "ai_video";
            try {
                const urlObj = new URL(videoState.videoUrl);
                const pathParts = urlObj.pathname.split("/");
                const lastPart = pathParts.pop()?.split("?")[0];
                if (lastPart) {
                    const dotIndex = lastPart.lastIndexOf(".");
                    potentialName = dotIndex > 0 ? lastPart.substring(0, dotIndex) : lastPart;
                }
            } catch (e) {
                console.warn("Could not parse video URL for filename", e);
            }

            const uniqueFileName = `${potentialName}_${Date.now()}`;
            const fileInfo = await this.convertToFileFromUrl(videoState.videoUrl, uniqueFileName);

            const fileToUpload: FileObj = {
                fileName: fileInfo.fileName,
                fileType: fileInfo.fileType,
                file: fileInfo.fileObj,
                fileId: `${fileInfo.fileName}-${fileInfo.fileObj.lastModified || Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                progress: 0,
                uploadStatus: UploadStatus.READY,
                formattedSize: formatBytes(fileInfo.fileObj.size, 2),
                fileSize: fileInfo.fileObj.size,
                skipAutoBackground: true
            };

            this.appService.setUploadingFiles([fileToUpload]);
            this.appService.uploadResourceFiles();

        } catch (error: any) {
            console.error("Error preparing video for upload:", error);
            this.toastr.error(`Failed to prepare video: ${error.message || "Unknown error"}`, "", TOAST_FASTER);
            this.appService.setUploadingStatus({ process: 0, show: false });
        }
    }

    /**
     * Create backend resource record after file upload succeeds
     */
    private createResourceByUploading(uploadedFile: FileObj): void {
        if (!uploadedFile?.fileId || !uploadedFile.fileName || uploadedFile.uploadStatus !== UploadStatus.SUCCESS) {
            console.error("createResourceByUploading: Invalid FileObj received.", uploadedFile);
            this.toastr.error("Failed to finalize upload due to missing file details.", "", TOAST_FASTER);
            this.appService.setUploadingStatus({ process: 0, show: false });
            if (uploadedFile?.fileId) this.processedFileIds.delete(uploadedFile.fileId);
            return;
        }

        const resourceName = uploadedFile.fileName;
        const resourceType = uploadedFile.fileType?.toLowerCase() ?? "unknown";

        const resourceData = {
            resource_name: resourceName,
            resource_type: resourceType,
            resource_total_time: "0",
            default_player: this.storeSelector.getBlockLabelByFileType(resourceType)?.id as any || "default",
            resource_date_created: Date.now().toString(),
            resource_date_modified: Date.now().toString(),
            resource_bytes_total: (uploadedFile.fileSize ?? 0).toString(),
            access_key: "0",
            tree_path: "",
        };

        const saveRecord: ICRUDRecord = {
            studioId: this.localStore.getStorageConfig()?.studioId,
            command: "update",
            primary: "resource_id",
            table: "resources",
            body: resourceData,
            id: uploadedFile.fileId,
        };

        if (!saveRecord.studioId) {
            console.error("createResourceByUploading: Missing studioId.");
            this.toastr.error("Configuration error: Studio ID is missing.", "", TOAST_FASTER);
            this.appService.setUploadingStatus({ process: 0, show: false });
            this.processedFileIds.delete(uploadedFile.fileId);
            return;
        }

        this.appService.crudRecord(saveRecord)
            .then((i_crudReply: ICRUDRecord | null) => {
                if (!i_crudReply?.serverReply?.status) {
                    console.error("Failed to update/create resource metadata:", i_crudReply?.serverReply);
                    this.toastr.error(this.translocoService.translate("toast.imageUploadFailed", {}, this.translocoService.getActiveLang()), "", TOAST_FASTER);
                    this.processedFileIds.delete(uploadedFile.fileId);
                    return;
                }

                const resourceId = String(uploadedFile.fileId);
                const changelistId = String(i_crudReply.serverReply.changelist_id ?? "");

                const finalResource: IResource = {
                    ...resourceData,
                    resource_id: resourceId,
                    id: resourceId,
                    changelist_id: changelistId,
                } as IResource;

                this.storeSelector.dispatch(actionAddResources({ data: finalResource }));
                this.toastr.info(this.translocoService.translate("toast.successUpload", {}, this.translocoService.getActiveLang()), "", TOAST_FASTER);

                // Reset uploading state immutably
                const images = this.aiImagesSubject.value;
                const resetImages = images.map(img => ({ ...img, uploading: false }));
                this.aiImagesSubject.next(resetImages);

                setTimeout(() => {
                    this.appService.setUploadingStatus({ process: 0, show: false });
                }, 1500);
            })
            .catch(error => {
                console.error("Error during resource metadata update/creation:", error);
                this.toastr.error("Failed to save resource details after upload.", "", TOAST_FASTER);
                this.appService.setUploadingStatus({ process: 0, show: false });
                this.processedFileIds.delete(uploadedFile.fileId);

                // Reset uploading state immutably
                const images = this.aiImagesSubject.value;
                const resetImages = images.map(img => ({ ...img, uploading: false }));
                this.aiImagesSubject.next(resetImages);
            });
    }

    /**
     * Extract image name from URL
     */
    private extractImageName(url: string): string {
        if (!url) return `ai_image_${Date.now()}`;
        try {
            const pathPart = url.split("?")[0].split("/").pop() ?? "";
            const dotIndex = pathPart.lastIndexOf(".");
            const name = dotIndex > 0 ? pathPart.substring(0, dotIndex) : pathPart;
            return name || `ai_image_${Date.now()}`;
        } catch (e) {
            console.warn("Could not parse URL to extract image name:", e);
            return `ai_image_${Date.now()}`;
        }
    }

    /**
     * Convert URL to File object
     */
    private async convertToFileFromUrl(url: string, filename: string): Promise<{
        fileObj: File;
        fileType: string;
        fileName: string
    }> {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status} ${response.statusText}`);
            }
            const blob = await response.blob();

            const fileType = blob.type || "application/octet-stream";
            let fileExtension = "";

            const commonTypes: { [key: string]: string } = {
                "video/mp4": "mp4", "video/webm": "webm", "video/ogg": "ogv",
                "image/jpeg": "jpg", "image/png": "png", "image/gif": "gif", "image/webp": "webp",
                "application/pdf": "pdf",
            };

            fileExtension = commonTypes[fileType] || fileType.split("/")[1] || "";

            const nameWithoutExtension = filename.includes(".") ? filename.substring(0, filename.lastIndexOf(".")) : filename;
            const finalFilename = fileExtension ? `${nameWithoutExtension}.${fileExtension}` : filename;

            const fileObj = new File([blob], finalFilename, { type: fileType });

            return { fileObj, fileType: fileExtension || fileType.split("/")[1] || "bin", fileName: finalFilename };

        } catch (error: any) {
            console.error("Error fetching file from URL:", url, error);
            throw new Error(`Failed to download file: ${error.message || "Network request failed"}`);
        }
    }

    /**
     * Convert base64 or URL to File object
     */
    private async convertToFileFromImageUrlOrBase64(urlOrBase64: string, fileName: string): Promise<{
        fileObj: File;
        fileType: string;
        fileName: string
    }> {
        try {
            let blob: Blob;
            let mimeType = 'image/png';

            if (urlOrBase64.startsWith('data:')) {
                // It's a base64 string - parse without regex to avoid stack overflow on large strings
                const commaIndex = urlOrBase64.indexOf(',');
                if (commaIndex === -1) {
                    throw new Error('Invalid base64 format - no comma found');
                }

                // Extract mime type from the data URL prefix
                const prefix = urlOrBase64.substring(0, commaIndex);
                const mimeMatch = prefix.match(/^data:([^;]+)/);
                if (mimeMatch) {
                    mimeType = mimeMatch[1];
                }

                // Extract base64 data (everything after the comma)
                const base64Data = urlOrBase64.substring(commaIndex + 1);

                // Convert base64 to blob using more efficient method
                const byteCharacters = atob(base64Data);
                const byteNumbers = new Uint8Array(byteCharacters.length);

                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }

                blob = new Blob([byteNumbers], { type: mimeType });
            } else {
                // It's a URL
                const response = await fetch(urlOrBase64);
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                blob = await response.blob();
                mimeType = blob.type || 'image/png';
            }

            // Extract file extension from mime type
            const fileType = mimeType.split('/')[1] || 'png';

            // Ensure fileName has correct extension
            const nameWithoutExtension = fileName.includes(".") ? fileName.substring(0, fileName.lastIndexOf(".")) : fileName;
            const finalFilename = `${nameWithoutExtension}.${fileType}`;

            const fileObj = new File([blob], finalFilename, { type: mimeType });

            return { fileObj, fileType, fileName: finalFilename };

        } catch (error: any) {
            console.error("Error converting image:", error);
            throw new Error(`Failed to convert image: ${error.message || "Unknown error"}`);
        }
    }

    /**
     * Set loading state
     */
    public setLoading(loading: boolean): void {
        this.loadingSubject.next(loading);
    }

    /**
     * Set pending mini mode action
     */
    public setPendingMiniModeAction(action: 'background' | 'block' | null): void {
        this.pendingMiniModeAction = action;
    }

    /**
     * Get pending mini mode action
     */
    public getPendingMiniModeAction(): 'background' | 'block' | null {
        return this.pendingMiniModeAction;
    }

    /**
     * Get current images
     */
    public getImages(): IAIImage[] {
        return this.aiImagesSubject.value;
    }

    /**
     * Get current video state
     */
    public getVideoState(): IVideoGenerationState {
        return this.videoStateSubject.value;
    }

    /**
     * Cleanup
     */
    ngOnDestroy(): void {
        this.stopVideoPolling();
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.processedFileIds.clear();
    }
}
